import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { AlertTriangle, Info, XCircle } from 'lucide-react';

const SystemBanner = () => {
    const [banner, setBanner] = useState(null);

    const fetchBanner = async () => {
        try {
            const { data, error } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'global_banner')
                .single();

            if (data?.value) {
                setBanner(data.value);
            }
        } catch (error) {
            console.error('Error fetching global banner:', error);
        }
    };

    useEffect(() => {
        fetchBanner();

        // Subscribe to changes
        const channel = supabase.channel('system_settings_banner')
            .on(
                'postgres_changes',
                { event: 'UPDATE', schema: 'public', table: 'system_settings', filter: 'key=eq.global_banner' },
                (payload) => {
                    if (payload.new && payload.new.value) {
                        setBanner(payload.new.value);
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
            // Cleanup height variable
            document.documentElement.style.removeProperty('--banner-height');
        };
    }, []);

    // Broadcast Height
    const bannerRef = React.useRef(null);
    useEffect(() => {
        if (!bannerRef.current) return;

        const updateHeight = () => {
            if (bannerRef.current) {
                const height = bannerRef.current.offsetHeight;
                document.documentElement.style.setProperty('--banner-height', `${height}px`);
            }
        };

        // Initial set
        updateHeight();

        // Observe resize
        const observer = new ResizeObserver(updateHeight);
        observer.observe(bannerRef.current);

        return () => observer.disconnect();
    }, [banner]); // Re-run when banner changes content/visibility

    if (!banner || !banner.active) return null;

    const getBannerStyles = (type) => {
        switch (type) {
            case 'error':
            case 'critical':
                return 'bg-red-600/95 text-white border-b border-red-500';
            case 'warning':
                return 'bg-amber-500/95 text-black border-b border-amber-400';
            default:
                return 'bg-blue-600/95 text-white border-b border-blue-500';
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case 'error':
            case 'critical':
                return <XCircle className="h-5 w-5 shrink-0" />;
            case 'warning':
                return <AlertTriangle className="h-5 w-5 shrink-0" />;
            default:
                return <Info className="h-5 w-5 shrink-0" />;
        }
    };

    return (
        <div ref={bannerRef} className={`w-full px-4 py-3 flex items-center justify-center shadow-lg backdrop-blur-sm z-[1000] relative ${getBannerStyles(banner.type)}`}>
            <div className="flex items-center gap-3 max-w-7xl mx-auto text-center font-medium">
                {getIcon(banner.type)}
                <span>{banner.message}</span>
            </div>
        </div>
    );
};

export default SystemBanner;
